import { memo, useCallback, useMemo } from 'react';

import { CheckoutConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { CheckoutConfirmationTable } from 'src/pages/Checkout/components/common/Table';
import { useTranslation } from 'react-i18next';
import { Enums } from 'src/models/enums';

type Props = {
  model: CheckoutConfirmationResponseType['AuctionGiftSubSection'];
};

export const CheckoutConfirmationAuctionGift = memo<Props>(({ model }) => {
  const tHelper = useTHelper('checkout.confirmation.auctionGift');

  const tipsOrProcessingColumnNameKey = useMemo(() => {
    const { HasTips, HasFees } = model.Items[0] || {};

    if (!HasTips && !HasFees) {
      return '';
    }

    if (HasTips && HasFees) {
      return 'tipOrProcessing';
    }

    return HasTips ? 'tip' : 'processing';
  }, [model.Items]);

  const columns = [
    { name: tHelper('amount'), className: 'left' },
    !!tipsOrProcessingColumnNameKey && { name: tHelper(tipsOrProcessingColumnNameKey), className: 'right' },
    { name: tHelper('status'), className: 'right' },
  ];

  const { t } = useTranslation();

  // PaymentStatusCode
  const getStatusCode = useCallback(
    (statusId: number): string => {
      const paymentStatusCode = Enums.contributions.PaymentStatusCode;
      switch (statusId) {
        case paymentStatusCode.Pending:
          return t('common.transactionStatuses.pending');
        case paymentStatusCode.Submitted:
          return t('common.transactionStatuses.submitted');
        case paymentStatusCode.Paid:
          return t('common.transactionStatuses.paid');
        case paymentStatusCode.Declined:
          return t('common.transactionStatuses.declined');
        case paymentStatusCode.Voided:
          return t('common.transactionStatuses.voided');
        case paymentStatusCode.Cancelled:
          return t('common.transactionStatuses.cancelled');
        case paymentStatusCode.Free:
          return t('common.transactionStatuses.free');
        case paymentStatusCode.AuthenticationRequired:
          return t('common.transactionStatuses.actionRequired');
        case paymentStatusCode.Recovery:
          return t('common.transactionStatuses.processing');
        case paymentStatusCode.Failed:
          return t('common.transactionStatuses.failed');
        default:
          return '';
      }
    },
    [t]
  );

  return (
    <>
      <h2 className="section-header">{model.SectionHeader}</h2>
      <CheckoutConfirmationTable columns={columns} data={model.Items}>
        {(item) => (
          <>
            <td className="left">{item.GiftAmount}</td>
            {tipsOrProcessingColumnNameKey && <td className="right">{item.TipsOrProcessingFeeAmount}</td>}
            <td className="right">{getStatusCode(item.PaymentStatusCode)}</td>
          </>
        )}
      </CheckoutConfirmationTable>
    </>
  );
});
